import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Link } from 'react-router-dom';
import { media, layout, styledUtils } from '@prisjakt/ui-base';
import { remCalc } from '@prisjakt/ui-base/styles/variables';
import RatingComponent from '@prisjakt/ui-rating';
import Button from '@prisjakt/ui-button';
import ProductCheckbox, {
  productCheckboxHoverStyle,
} from '@prisjakt/ui-product-checkbox';

interface IHasDivider {
  hasDivider: boolean;
}

export const Card = styled.div.attrs({ 'data-test': 'ProductCard' })<
  IHasDivider
>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 0 50%;
  align-self: stretch;
  width: 100%;
  min-height: 100%;
  min-width: ${layout.productCardMinWidthMobile};
  max-width: ${layout.productCardMaxWidth};
  text-align: ${ifProp('textCenter', 'center', 'left')};
  border-bottom: ${p =>
    p.hasDivider ? `1px solid ${p.theme.lines.primary}` : 'none'};

  ${media.mediumUp`
    flex: 1 0 ${layout.productCardMinWidth};
  `};
`;

interface ISelectable {
  selected: boolean;
}

export const ProductLink = styled(Link)<ISelectable>`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  position: relative;
  outline: none;
  padding: ${layout.productCardPaddingSmall};
  ${media.mediumUp`
    padding: ${layout.productCardPaddingLarge};
  `}

  ${props =>
    props.selected &&
    css`
      &::before {
        ${styledUtils.overlay(props.theme.bg.productCompareLight)};
      }
    `}

  @media (pointer: fine) {
    &:hover::after,
    &.focus-visible::after {
      ${styledUtils.overlay()}
    }
    ${/* sc-selector */ Card}:focus-within &::after {
      ${styledUtils.overlay()}
    }
  }
`;

export const CampaignBadge = styled.span`
  max-height: 55px;
  max-width: 55px;
  position: absolute;
  top: 0;
  left: 0;
  padding: ${layout.productCardPaddingSmall};
  ${media.mediumUp`
    padding: ${layout.productCardPaddingLarge};
  `}
`;

export const ProductImage = styled.div`
  align-items: center;
  box-sizing: content-box;
  order: 0;
  text-align: center;
  height: ${layout.productCardImageSize};
  width: ${layout.productCardImageSize};
  margin: 0 auto 20px auto;
  background: ${p => p.theme.colors.base200};
  border-radius: ${layout.borderRadius};

  img {
    max-height: 100%;
  }
`;

export const BrandLogo = styled.span`
  max-height: 55px;
  max-width: 55px;
  position: absolute;
  top: 0;
  right: 0;
  padding: 1rem;
`;

export const ProductDetails = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: ${ifProp('stretch', '1 0 auto', '0 0 auto')};
  padding: 0 ${layout.measurements.spacing025};
  ${media.mediumUp`
    padding: 0;
  `}
`;

export const Rating = styled(RatingComponent)`
  position: absolute;
  bottom: 100%;
  padding: 2px;
  align-self: center;
  background: ${p => p.theme.colors.base0};
`;

export const ProductName = styled.div.attrs({ 'data-test': 'ProductName' })`
  overflow-wrap: break-word;
  ${media.mediumUp`
    margin-top: ${layout.measurements.spacing025};
  `}
`;

export const Subtitle = styled.div`
  color: ${p => p.theme.text.tertiary};
  font-size: ${remCalc(13)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${media.mediumUp`
    font-size: ${remCalc(12)};
  `};
`;

export const ExtraFooter = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  font-size: 13px;
  color: ${p => p.theme.text.tertiary};

  ${media.mediumUp`
    font-size: 12px;
  `};
`;

export const Footer = styled.div`
  display: block;
  position: relative;
  outline: none;

  @media (pointer: fine) {
    ${/* sc-selector */ ProductLink}:hover + &::after,
    ${/* sc-selector */ ProductLink}:focus + &::after {
      ${styledUtils.overlay()}
    }
  }
`;

export const CategoryArea = styled.div`
  display: block;
  padding: 0 1rem 1rem;
  position: relative;
  outline: none;

  @media (pointer: fine) {
    ${/* sc-selector */ ProductLink}:hover + &::after,
    ${/* sc-selector */ ProductLink}:focus + &::after {
      ${styledUtils.overlay()}
    }
  }
`;

export const CategoryLink = styled(Button)`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  box-sizing: border-box;

  && {
    text-transform: none;
  }

  &:hover,
  &:focus {
    color: ${p => p.theme.text.callToActionHover};
  }
`;

export const DiffPercentageCore = styled.div`
  background: ${p => p.theme.colors.redio500};
  ${media.mediumUp`
    top: ${layout.productCardPaddingLarge};
    left: ${layout.productCardPaddingLarge};
  `}
  padding: 0.1rem 0.4rem;
  font-weight: 600;
  color: ${p => p.theme.colors.base0};
  border-radius: ${layout.borderRadius};
`;

export const DiffPercentage = styled(DiffPercentageCore)`
  position: absolute;
  top: ${layout.productCardPaddingSmall};
  left: ${layout.productCardPaddingSmall};
`;

export const StoreLogo = styled.div`
  margin-left: 10px;
  max-width: 40%;
`;

export const PriceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${layout.measurements.spacing025};
  ${media.mediumUp`
    padding: 0;
    margin-top: ${layout.measurements.spacing025};
  `}
`;

export const Checkbox = styled(ProductCheckbox).attrs(() => ({
  vertical: false,
}))`
  position: absolute;
  top: 10px;
  left: 10px;

  ${/* sc-selector */ Card}:hover & {
    ${productCheckboxHoverStyle}
  }
`;
